// Plain-language wording for the form Activity panel (dform_audit_log rows).
// describeActivity(entry) returns what happened, without who or when, e.g.
// `published the form` or `renamed the form from "A" to "B"`.

import { formatWallClock } from "./indiaTime";

export const ACTIVITY_FILTERS = [
    { value: "", label: "All activity" },
    { value: "form_saved", label: "Saves" },
    { value: "form_published", label: "Published" },
    { value: "form_unpublished", label: "Unpublished" },
    { value: "form_closed", label: "Closed" },
    { value: "form_reopened", label: "Reopened" },
    { value: "form_renamed", label: "Renamed" },
    { value: "settings_changed", label: "Settings changed" },
    { value: "page_added", label: "Page added" },
    { value: "page_deleted", label: "Page deleted" },
    { value: "page_reordered", label: "Pages reordered" },
    { value: "responses_exported", label: "Responses exported" },
    { value: "response_deleted", label: "Response deleted" },
    { value: "audience_changed", label: "Who can fill changed" },
    { value: "reminder_sent", label: "Reminders sent" },
    { value: "form_created", label: "Created" },
    { value: "form_duplicated", label: "Duplicated" },
];

const onOff = (value) => (value ? "on" : "off");
const quote = (value) => `"${value ?? ""}"`;
const plural = (n, word) => `${n} ${word}${Number(n) === 1 ? "" : "s"}`;

function pageName(details) {
    if (details.page_title) return `page ${quote(details.page_title)}`;
    if (details.page === "start") return "the Start page";
    if (details.page === "end") return "the End page";
    return "a page";
}

// Open/closing dates are the India wall-clock values the owner picked
// ("YYYY-MM-DDTHH:mm"): shown as they are, never shifted by a time zone.
function describeDate(value) {
    if (!value) return "not set";
    return formatWallClock(value);
}

function describeSetting(d) {
    switch (d.setting) {
        case "show_progress_bar":
            return `turned the progress bar ${onOff(d.new)}`;
        case "respond_notifications":
            return `turned email notifications for new responses ${onOff(d.new)}`;
        case "save_response":
            return `turned the "save my response as PDF" option ${onOff(d.new)}`;
        case "submission_limit":
            if (d.new === null || d.new === undefined) return "removed the response limit";
            if (d.old === null || d.old === undefined) return `set the response limit to ${d.new}`;
            return `changed the response limit from ${d.old} to ${d.new}`;
        case "form_dates": {
            const next = d.new || {};
            if (!next.open && !next.close) return "removed the open and close dates";
            return `set the form to open ${describeDate(next.open)} and close ${describeDate(next.close)}`;
        }
        case "close_message":
            return "changed the message shown when the form is closed";
        case "offer_as_template":
            return `turned "Offer this form as a template" ${onOff(d.new)}`;
        case "allow_multiple_responses":
            return `turned "Allow more than one response per person" ${onOff(d.new)}`;
        case "allow_edit_response":
            return `turned "Let people edit their response" ${onOff(d.new)}`;
        case "auto_reminder":
            return `turned the automatic reminder ${quote(d.label || d.kind)} ${onOff(d.new)}`;
        case "workflow": {
            if (!d.new) return d.old ? `turned off the ${quote(d.old.name)} email` : "turned off the follow-up email";
            const delay = d.new.delay_minutes ? ` (sent after ${plural(d.new.delay_minutes, "minute")})` : "";
            return `set the follow-up email to ${quote(d.new.name)}${delay}`;
        }
        default:
            return `changed the ${String(d.setting || "form").replace(/_/g, " ")} setting`;
    }
}

export function describeActivity(entry) {
    const d = (entry && entry.details) || {};
    switch (entry && entry.action) {
        case "form_created":
            return d.from_template_id ? "created the form from a template" : "created the form";
        case "form_renamed":
            return `renamed the form from ${quote(d.from)} to ${quote(d.to)}`;
        case "form_saved":
            return `saved ${pageName(d)}${d.version ? ` (version ${d.version})` : ""}`;
        case "form_published":
            return "published the form";
        case "form_unpublished":
            return "unpublished the form";
        case "form_closed":
            return "closed the form to new responses";
        case "form_reopened":
            return "reopened the form for responses";
        case "settings_changed":
            return describeSetting(d);
        case "page_added":
            return d.copied_from
                ? `added page ${quote(d.title)} (a copy of ${quote(d.copied_from)}${d.from_form_id ? " from another form" : ""})`
                : `added page ${quote(d.title)}`;
        case "page_deleted":
            return `deleted page ${quote(d.title)}`;
        case "page_reordered":
            return Array.isArray(d.to) ? `reordered the pages: ${d.to.join(", ")}` : "reordered the pages";
        case "form_duplicated":
            return d.from_title ? `created this form as a copy of ${quote(d.from_title)}` : "created this form as a copy of another form";
        case "form_deleted":
            return "deleted the form";
        case "responses_exported": {
            const format = { excel: "Excel", pdf: "PDF", csv: "CSV" }[d.format] || "a file";
            return d.count === null || d.count === undefined
                ? `exported responses as ${format}`
                : `exported ${plural(d.count, "response")} as ${format}`;
        }
        case "response_deleted":
            return d.response_id ? `deleted response #${d.response_id}` : "deleted a response";
        case "audience_changed":
            return "changed who can fill this form";
        case "reminder_sent": {
            const how = d.mode === "auto" ? "automatic" : "manual";
            return `sent ${plural(d.count ?? 0, `${how} reminder`)}`;
        }
        default:
            return String((entry && entry.action) || "did something").replace(/_/g, " ");
    }
}

export function activityWho(entry) {
    return (entry && (entry.name || entry.emp_id)) || "Someone";
}
